import type { ReactNode } from 'react';
import type { ModeState } from '../../lib/api.ts';
import { Spot } from './kit.tsx';

/**
 * Pictures of each dashboard screen, for the guide.
 *
 * Each one mirrors the markup of the real page (same classes, same labels,
 * same column order) filled with example numbers, so the picture looks like
 * the screen the reader will open. When a page changes, its picture here
 * should change with it.
 *
 * Example data is consistent with itself — a 1.62% net gap on 500 USDT shows
 * 8.10 USDT profit — because a reader who checks one number and finds it
 * wrong stops trusting the rest.
 */

const TABS = ['Opportunities', 'Phase 0', 'Transfers', 'Balances & feeds', 'Trade log', 'Settings', 'Guide'];

function Field({
  label,
  value,
  width,
  type = 'text',
  placeholder,
}: {
  label: string;
  value: string | number;
  width?: number;
  type?: 'text' | 'number' | 'password';
  placeholder?: string;
}) {
  return (
    <div className="field" style={width ? { width, marginBottom: 0 } : undefined}>
      <label>{label}</label>
      <input type={type} defaultValue={value} placeholder={placeholder} readOnly tabIndex={-1} />
    </div>
  );
}

function Choice({ label, value, width }: { label: string; value: string; width: number }) {
  return (
    <div style={{ width }}>
      <label>{label}</label>
      <select defaultValue={value} tabIndex={-1}>
        <option>{value}</option>
      </select>
    </div>
  );
}

function Tick({ label, checked }: { label: string; checked: boolean }) {
  return (
    <label>
      <input type="checkbox" defaultChecked={checked} tabIndex={-1} style={{ width: 'auto', marginRight: 8 }} />
      {label}
    </label>
  );
}

/** Mirrors components/ModeBanner.tsx, using the live switch values when known. */
function Banner({ mode, annotate }: { mode: ModeState | null; annotate: boolean }) {
  const live = mode?.executionEnabled ?? false;
  const label = mode?.info.label ?? 'Transfer (buy → withdraw → sell)';
  const measuring = mode ? mode.pocMeasurement : true;
  const transfer = mode ? mode.transfer : { minNetPct: 1.5, expectedMinutes: 10 };
  const mark = (n: number, node: ReactNode) => (annotate ? <Spot n={n}>{node}</Spot> : node);

  return (
    <div className={`mode-banner ${live ? 'live' : 'safe'}`}>
      {mark(4, <span className="mode-chip">{label}</span>)}
      {mark(
        5,
        <span className={live ? 'red' : 'green'}>
          <strong>{live ? '● EXECUTION LIVE' : '○ detecting only'}</strong>
        </span>,
      )}
      <span className="dim mode-note">
        {live ? 'Real orders can be placed with real money.' : 'No orders will be placed. The master guard is off.'}
      </span>
      {measuring && mark(6, <span className="pill">Phase 0 measuring</span>)}
      {transfer &&
        mark(
          7,
          <span className="faint mode-note">
            Surfacing ≥{transfer.minNetPct}% net · ~{transfer.expectedMinutes} min transit
          </span>,
        )}
    </div>
  );
}

/** The top bar and status strip every screen sits inside. */
export function AppFrame({
  tab,
  mode,
  annotate = false,
  feeds = '4/4',
  children,
}: {
  tab: string;
  mode: ModeState | null;
  annotate?: boolean;
  feeds?: string;
  children: ReactNode;
}) {
  const allLive = feeds.split('/')[0] === feeds.split('/')[1];
  const nav = (
    <div className="nav">
      {TABS.map((t) => (
        <button key={t} type="button" tabIndex={-1} className={t === tab ? 'active' : ''}>
          {t}
        </button>
      ))}
    </div>
  );
  const feedCount = (
    <span>
      <span className={`dot ${allLive ? 'live' : 'reconnecting'}`} />
      {feeds} feeds
    </span>
  );
  const signOut = (
    <button type="button" tabIndex={-1} className="btn small">
      Sign out
    </button>
  );

  return (
    <div className="app-mock">
      <div className="topbar">
        <div className="brand">
          Arbitrage<span>.</span>
        </div>
        {annotate ? (
          <Spot n={1} block>
            {nav}
          </Spot>
        ) : (
          nav
        )}
        <div className="topbar-right">
          {annotate ? <Spot n={2}>{feedCount}</Spot> : feedCount}
          <span className="faint">you</span>
          {annotate ? <Spot n={3}>{signOut}</Spot> : signOut}
        </div>
      </div>
      <Banner mode={mode} annotate={annotate} />
      <div className="mock-main">{children}</div>
    </div>
  );
}

// ------------------------------------------------------------------- sign in

export function LoginMock() {
  return (
    <div className="login-wrap">
      <div className="login-card">
        <div className="brand">
          Arbitrage<span>.</span>
        </div>
        <div className="tagline">Private single-operator tool</div>
        <div className="card">
          <div className="card-body">
            <Spot n={1} block>
              <Field label="Username" value="your-name" />
            </Spot>
            <Spot n={2} block>
              <div className="field">
                <label>Password</label>
                <div className="input-with-action">
                  <input type="password" defaultValue="example-password" readOnly tabIndex={-1} />
                  <span className="reveal">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.6">
                      <path d="M1.8 12S5.4 5.4 12 5.4 22.2 12 22.2 12 18.6 18.6 12 18.6 1.8 12 1.8 12Z" />
                      <circle cx="12" cy="12" r="3.2" />
                    </svg>
                  </span>
                </div>
              </div>
            </Spot>
            <Spot n={3} block>
              <Field label="Two-factor code" value="482913" />
            </Spot>
            <Spot n={4} block>
              <button type="button" tabIndex={-1} className="btn primary" style={{ width: '100%' }}>
                Sign in
              </button>
            </Spot>
          </div>
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------- layout tour

export function LayoutMock({ mode }: { mode: ModeState | null }) {
  return (
    <AppFrame tab="Opportunities" mode={mode} annotate>
      <div className="page-head">
        <div>
          <h1>Live opportunities</h1>
          <div className="sub">Net of both taker fees AND the flat withdrawal fee.</div>
        </div>
      </div>
      <div className="card">
        <div className="empty" style={{ padding: '26px 20px' }}>
          <strong>No opportunities above the threshold right now.</strong>
          An empty list here is the normal state — spreads that clear every fee are rare and short-lived.
        </div>
      </div>
    </AppFrame>
  );
}

// ------------------------------------------------------------ opportunities

const OPPS = [
  { sym: 'ENA/USDT', buy: 'mexc', bp: '0.4210', sell: 'kucoin', sp: '0.4302', gross: '2.19%', net: '1.62%', drag: '0.37%', min: '93', transit: '~10m', profit: '8.10', age: '0.4s', expired: false },
  { sym: 'SUI/USDT', buy: 'okx', bp: '3.412', sell: 'binance', sp: '3.468', gross: '1.64%', net: '1.21%', drag: '0.23%', min: '80', transit: '~5m', profit: '6.05', age: '1.2s', expired: false },
  { sym: 'PEPE/USDT', buy: 'kucoin', bp: '0.00001021', sell: 'mexc', sp: '0.00001039', gross: '1.76%', net: '1.08%', drag: '0.48%', min: '154', transit: '~10m', profit: '5.40', age: '3.8s', expired: true },
];

function ExecButton({ expired }: { expired: boolean }) {
  return (
    <button type="button" tabIndex={-1} className="btn exec small" disabled={expired}>
      {expired ? 'expired' : 'Execute'}
    </button>
  );
}

export function OpportunitiesMock({ mode }: { mode: ModeState | null }) {
  return (
    <AppFrame tab="Opportunities" mode={mode}>
      <div className="page-head">
        <div>
          <h1>Live opportunities</h1>
          <div className="sub">
            Net of both taker fees AND the flat withdrawal fee. The sell price can still move while the coin is in
            transit.
          </div>
        </div>
        <div className="sub">
          <Spot n={3}>
            <span className="green">
              <span className="dot live" />
              streaming
            </span>
          </Spot>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <Spot n={1} block>
            <div className="row">
              <Field label="Min net %" type="number" value={0} width={110} />
              <Field label="Pair" value="" placeholder="BTC" width={130} />
              <Choice label="Exchange" value="any" width={130} />
              <Choice label="Sort by" value="net profit" width={130} />
            </div>
          </Spot>
          <Spot n={2}>
            <span className="dim" style={{ fontSize: 13 }}>
              3 of 3
            </span>
          </Spot>
        </div>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Pair</th>
                <th>
                  <Spot n={4}>Buy on</Spot>
                </th>
                <th className="num">Buy price</th>
                <th>
                  <Spot n={5}>Sell on</Spot>
                </th>
                <th className="num">Sell price</th>
                <th className="num">Gross</th>
                <th className="num">
                  <Spot n={6}>Net</Spot>
                </th>
                <th className="num">
                  <Spot n={7}>Fee drag</Spot>
                </th>
                <th className="num">Min size</th>
                <th className="num">Transit</th>
                <th className="num">
                  <Spot n={8}>Est. profit</Spot>
                </th>
                <th className="num">
                  <Spot n={9}>Age</Spot>
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {OPPS.map((o, i) => (
                <tr key={o.sym} style={o.expired ? { opacity: 0.4 } : undefined}>
                  <td className="sym">{o.sym}</td>
                  <td>{o.buy}</td>
                  <td className="num">{o.bp}</td>
                  <td>{o.sell}</td>
                  <td className="num">{o.sp}</td>
                  <td className="num dim">{o.gross}</td>
                  <td className="num green">
                    <strong>{o.net}</strong>
                  </td>
                  <td className="num amber">{o.drag}</td>
                  <td className="num faint">{o.min}</td>
                  <td className="num faint">{o.transit}</td>
                  <td className="num">{o.profit} USDT</td>
                  <td className="num faint">{o.age}</td>
                  <td>
                    {i === 0 ? (
                      <Spot n={10}>
                        <ExecButton expired={false} />
                      </Spot>
                    ) : (
                      <ExecButton expired={o.expired} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AppFrame>
  );
}

/** Mirrors components/ExecuteModal.tsx for the first example row. */
export function ExecuteMock() {
  return (
    <div className="mock-stage">
      <div className="modal">
        <div className="modal-head">
          Confirm trade — <span className="mono">ENA/USDT</span>
        </div>
        <div className="modal-body">
          <Spot n={1} block>
            <div className="kv">
              <span className="k">Buy on</span>
              <span className="v">mexc @ 0.4210</span>
            </div>
            <div className="kv">
              <span className="k">Sell on</span>
              <span className="v">kucoin @ 0.4302</span>
            </div>
          </Spot>
          <div className="kv">
            <span className="k">Gross gap</span>
            <span className="v dim">2.19%</span>
          </div>
          <Spot n={2} block>
            <div className="kv">
              <span className="k">Net after fees + buffer</span>
              <span className="v green">
                <strong>1.62%</strong>
              </span>
            </div>
          </Spot>
          <Spot n={3} block>
            <Field label="Trade size (USDT)" type="number" value={500} />
          </Spot>
          <Spot n={4} block>
            <div className="kv">
              <span className="k">Estimated net profit</span>
              <span className="v green">
                <strong>8.10 USDT</strong>
              </span>
            </div>
          </Spot>
          <Spot n={5} block>
            <div className="countdown">
              <div style={{ width: '64%' }} />
            </div>
            <div className="faint" style={{ fontSize: 12, marginTop: 6 }}>
              Quote valid for another 3.2s
            </div>
          </Spot>
        </div>
        <div className="modal-foot">
          <button type="button" tabIndex={-1} className="btn">
            Cancel
          </button>
          <Spot n={6}>
            <button type="button" tabIndex={-1} className="btn exec">
              Confirm — buy mexc, sell kucoin
            </button>
          </Spot>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ phase 0

const HORIZONS = [
  { h: 1, measured: 9, missing: 0, still: 6, even: 8, median: '1.71%', mean: '1.52%', p25: '0.94%', p75: '2.10%', size: 420 },
  { h: 5, measured: 9, missing: 0, still: 5, even: 7, median: '1.55%', mean: '1.31%', p25: '0.60%', p75: '1.98%', size: 390 },
  { h: 10, measured: 8, missing: 1, still: 4, even: 6, median: '1.49%', mean: '1.20%', p25: '0.41%', p75: '1.90%', size: 380 },
  { h: 15, measured: 7, missing: 2, still: 3, even: 5, median: '1.12%', mean: '0.94%', p25: '0.18%', p75: '1.71%', size: 350 },
  { h: 30, measured: 7, missing: 2, still: 2, even: 4, median: '0.72%', mean: '0.55%', p25: '−0.40%', p75: '1.40%', size: 310 },
];

function rateClass(rate: number): string {
  if (rate >= 0.5) return 'green';
  if (rate >= 0.25) return 'amber';
  return 'red';
}

export function Phase0Mock({ mode }: { mode: ModeState | null }) {
  return (
    <AppFrame tab="Phase 0" mode={mode}>
      <div className="page-head">
        <div>
          <h1>Phase 0 — gap persistence</h1>
          <div className="sub">
            Read-only measurement. No keys, no orders. Does an arbitrage gap survive the 2–30 minutes a coin spends in
            transit?
          </div>
        </div>
        <div className="row">
          <Spot n={1}>
            <Field label="Window (hours)" type="number" value={168} width={120} />
          </Spot>
          <Spot n={2}>
            <Field label="Threshold %" type="number" value={1.5} width={110} />
          </Spot>
          <Spot n={3}>
            <button type="button" tabIndex={-1} className="btn">
              Refresh
            </button>
          </Spot>
        </div>
      </div>

      <Spot n={4} block>
        <div className="grid cols-4">
          <div className="card stat">
            <div className="label">Episodes recorded</div>
            <div className="value">61,870</div>
            <div className="hint">3 still being tracked</div>
          </div>
          <div className="card stat">
            <div className="label">Qualifying</div>
            <div className="value">1,350</div>
            <div className="hint">≥ 1.5% net at detection</div>
          </div>
          <div className="card stat">
            <div className="label">Collection window</div>
            <div className="value">504.0h</div>
            <div className="hint">Target: 2–3 days of continuous data</div>
          </div>
          <div className="card stat">
            <div className="label">Horizons</div>
            <div className="value" style={{ fontSize: 16 }}>
              T+1 · T+5 · T+10 · T+15 · T+30
            </div>
            <div className="hint">minutes after detection</div>
          </div>
        </div>
      </Spot>

      <Spot n={5} block>
        <div className="notice warn" style={{ marginBottom: 0 }}>
          <strong>INCONCLUSIVE — too few qualifying gaps in this window to decide</strong>
          <ul style={{ margin: '8px 0 0', paddingLeft: 18, lineHeight: 1.6 }}>
            <li>9 gaps cleared 1.5% in the last 168 hours</li>
            <li>At T+10, 4 of the 8 measured were still above 1.5%</li>
          </ul>
        </div>
      </Spot>

      <div className="card">
        <div className="card-head">
          <h2>Decay curve</h2>
          <span className="faint" style={{ fontSize: 12 }}>
            Buy price locked at T0 · sell price re-read at each horizon
          </span>
        </div>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Horizon</th>
                <th className="num">Measured</th>
                <th className="num">
                  <Spot n={8}>Missing</Spot>
                </th>
                <th className="num">
                  <Spot n={6}>Still ≥1.5%</Spot>
                </th>
                <th className="num">
                  <Spot n={7}>Still ≥0%</Spot>
                </th>
                <th className="num">Median net</th>
                <th className="num">Mean net</th>
                <th className="num">P25</th>
                <th className="num">P75</th>
                <th className="num">Exec size</th>
              </tr>
            </thead>
            <tbody>
              {HORIZONS.map((h) => (
                <tr key={h.h}>
                  <td className="sym">T+{h.h}m</td>
                  <td className="num">{h.measured}</td>
                  <td className={`num ${h.missing > 0 ? 'amber' : 'faint'}`}>{h.missing}</td>
                  <td className={`num ${rateClass(h.still / h.measured)}`}>
                    {Math.round((h.still / h.measured) * 100)}% ({h.still})
                  </td>
                  <td className={`num ${rateClass(h.even / h.measured)}`}>
                    {Math.round((h.even / h.measured) * 100)}% ({h.even})
                  </td>
                  <td className="num green">{h.median}</td>
                  <td className="num green">{h.mean}</td>
                  <td className="num faint">{h.p25}</td>
                  <td className="num faint">{h.p75}</td>
                  <td className="num">{h.size}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>
            <Spot n={9}>By coin</Spot>
          </h2>
          <span className="faint" style={{ fontSize: 12 }}>
            Expect heavy concentration in a few thin names
          </span>
        </div>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Coin</th>
                <th className="num">Episodes</th>
                <th className="num">Qualifying</th>
                <th className="num">Median net T0</th>
                <th className="num">Best net T0</th>
                <th className="num">Survived</th>
                <th className="num">Withdrawal fee</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="sym">ENA</td>
                <td className="num">3,120</td>
                <td className="num">4</td>
                <td className="num green">0.41%</td>
                <td className="num green">2.31%</td>
                <td className="num">50%</td>
                <td className="num faint">1.1 ENA</td>
              </tr>
              <tr>
                <td className="sym">SUI</td>
                <td className="num">2,040</td>
                <td className="num">3</td>
                <td className="num green">0.33%</td>
                <td className="num green">1.84%</td>
                <td className="num">67%</td>
                <td className="num faint">0.12 SUI</td>
              </tr>
              <tr>
                <td className="sym">PEPE</td>
                <td className="num">1,610</td>
                <td className="num">2</td>
                <td className="num green">0.29%</td>
                <td className="num green">1.76%</td>
                <td className="num">0%</td>
                <td className="num faint">1300000 PEPE</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </AppFrame>
  );
}

// ---------------------------------------------------------------- transfers

function Steps({ done, current, failed = false }: { done: number; current?: number; failed?: boolean }) {
  const labels = ['buying', 'bought', 'sending', 'in transit', 'arrived', 'selling'];
  return (
    <div className="steps">
      {labels.map((label, i) => {
        const cls = i === current ? (failed ? 'step bad' : 'step current') : i < done ? 'step done' : 'step';
        return (
          <span key={label} className={cls}>
            {label}
          </span>
        );
      })}
    </div>
  );
}

export function TransfersMock({ mode }: { mode: ModeState | null }) {
  const timeout = mode?.transfer?.timeoutMinutes ?? 60;
  return (
    <AppFrame tab="Transfers" mode={mode}>
      <div className="page-head">
        <div>
          <h2>Transfers</h2>
          <p className="sub">
            Buy, send, sell. The owner is exposed to the coin from the moment the buy fills until it sells.
          </p>
        </div>
      </div>

      <h3 className="section-title bad">
        <Spot n={1}>Holding — needs a decision</Spot>
      </h3>
      <div className="card danger">
        <div className="card-head">
          <strong>PEPE · kucoin → mexc</strong>
          <Spot n={4}>
            <span className="pill bad">TIMED_OUT_HOLDING</span>
          </Spot>
        </div>
        <Steps done={0} />
        <div className="grid">
          <span>size</span>
          <span>500.00 USDT</span>
          <span>expected</span>
          <span>1.08%</span>
          <span>bought</span>
          <span>48971596 PEPE @ 0.00001021</span>
          <span>withdrawal</span>
          <span className="mono">wd-20877</span>
        </div>
        <p className="reason">Deposit not seen on mexc within {timeout} minutes.</p>
        <p className="reason">This run stopped while holding PEPE. Nothing further happens automatically.</p>
      </div>

      <h3 className="section-title">Recent (2)</h3>
      <div className="card">
        <div className="card-head">
          <strong>SUI · okx → binance</strong>
          <span className="pill warn">IN_TRANSIT</span>
        </div>
        <Spot n={2} block>
          <Steps done={3} current={3} />
        </Spot>
        <Spot n={3} block>
          <div className="grid">
            <span>size</span>
            <span>500.00 USDT</span>
            <span>expected</span>
            <span>1.21%</span>
            <span>bought</span>
            <span>146.54 SUI @ 3.412</span>
            <span>withdrawal</span>
            <span className="mono">wd-20931</span>
          </div>
        </Spot>
      </div>
      <div className="card">
        <div className="card-head">
          <strong>ENA · mexc → kucoin</strong>
          <span className="pill ok">COMPLETED</span>
        </div>
        <Steps done={6} />
        <div className="grid">
          <span>size</span>
          <span>500.00 USDT</span>
          <span>received</span>
          <span>1183.2 ENA</span>
          <span>net on arrival</span>
          <span>1.48%</span>
          <span>P&amp;L</span>
          <span className="green">+7.40 USDT</span>
        </div>
      </div>

      <h3 className="section-title">
        <Spot n={5}>Deposit addresses</Spot>
      </h3>
      <table>
        <thead>
          <tr>
            <th>Receiving venue</th>
            <th>Coin</th>
            <th>Network</th>
            <th>Address</th>
            <th>Whitelisted</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>kucoin</td>
            <td>ENA</td>
            <td>ERC20</td>
            <td className="mono">0x7a3f…91c2</td>
            <td>
              <Spot n={6}>
                <span className="pill ok">yes</span>
              </Spot>
            </td>
          </tr>
          <tr>
            <td>mexc</td>
            <td>PEPE</td>
            <td>ERC20</td>
            <td className="mono">0x4be0…0d7a</td>
            <td>
              <span className="pill bad">not confirmed</span>
            </td>
          </tr>
        </tbody>
      </table>
    </AppFrame>
  );
}

// --------------------------------------------------------- balances & feeds

const FEEDS = [
  { venue: 'binance', state: 'live', last: '0.2s', fresh: '118/118', full: true, reconnects: 0, error: '—' },
  { venue: 'okx', state: 'live', last: '0.1s', fresh: '96/96', full: true, reconnects: 0, error: '—' },
  { venue: 'mexc', state: 'live', last: '0.6s', fresh: '139/142', full: false, reconnects: 2, error: '—' },
  { venue: 'kucoin', state: 'reconnecting', last: '8.4s', fresh: '0/88', full: false, reconnects: 3, error: 'connection reset by peer' },
];

export function BalancesMock({ mode }: { mode: ModeState | null }) {
  return (
    <AppFrame tab="Balances & feeds" mode={mode} feeds="3/4">
      <div className="page-head">
        <div>
          <h1>Balances &amp; feeds</h1>
          <div className="sub">Is the system seeing the market, and where is the money?</div>
        </div>
        <Spot n={1}>
          <button type="button" tabIndex={-1} className="btn small">
            Refresh
          </button>
        </Spot>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Feed health</h2>
          <span className="faint" style={{ fontSize: 12 }}>
            a venue that is not live is excluded from opportunity calculation
          </span>
        </div>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Venue</th>
                <th>
                  <Spot n={2}>State</Spot>
                </th>
                <th className="num">Last update</th>
                <th className="num">
                  <Spot n={3}>Fresh pairs</Spot>
                </th>
                <th className="num">
                  <Spot n={4}>Reconnects</Spot>
                </th>
                <th>Last error</th>
              </tr>
            </thead>
            <tbody>
              {FEEDS.map((f) => (
                <tr key={f.venue}>
                  <td>
                    <strong>{f.venue}</strong>
                  </td>
                  <td>
                    <span className={`dot ${f.state}`} />
                    {f.state}
                  </td>
                  <td className="num">{f.last}</td>
                  <td className={`num ${f.full ? 'green' : 'amber'}`}>{f.fresh}</td>
                  <td className={`num ${f.reconnects > 0 ? 'amber' : 'faint'}`}>{f.reconnects}</td>
                  <td className="faint">{f.error}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid cols-2">
        <div className="card">
          <div className="card-head">
            <h2>Binance</h2>
            <Spot n={5}>
              <span className="pill ok">connected</span>
            </Spot>
          </div>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Asset</th>
                  <th className="num">
                    <Spot n={6}>Free</Spot>
                  </th>
                  <th className="num">In orders</th>
                  <th className="num">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="sym">USDT</td>
                  <td className="num">500.00</td>
                  <td className="num faint">—</td>
                  <td className="num">500.00</td>
                </tr>
                <tr>
                  <td className="sym">BNB</td>
                  <td className="num">0.012300</td>
                  <td className="num faint">—</td>
                  <td className="num">0.012300</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div className="card">
          <div className="card-head">
            <h2>KuCoin</h2>
            <span className="pill bad">error</span>
          </div>
          <div className="card-body">
            <div className="notice bad" style={{ margin: 0 }}>
              Invalid API key or permissions
              <div className="faint" style={{ marginTop: 8, fontSize: 12 }}>
                Usually one of: the key lacks read permission, the venue requires an IP allowlist entry for this
                server, or the server clock has drifted enough to invalidate the request signature.
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppFrame>
  );
}

// ---------------------------------------------------------------- trade log

const EPISODES = [
  { at: '14:02:11', sym: 'ENA/USDT', route: 'mexc→kucoin', gross: '2.19%', net: '1.62%', peak: '1.80%', life: 0.4, alerted: 'yes' },
  { at: '13:47:55', sym: 'SUI/USDT', route: 'okx→binance', gross: '1.64%', net: '1.21%', peak: '1.21%', life: 0.1, alerted: 'no' },
  { at: '11:20:03', sym: 'PEPE/USDT', route: 'kucoin→mexc', gross: '1.76%', net: '1.08%', peak: '1.33%', life: 2.6, alerted: 'yes' },
];

export function TradeLogMock({ mode }: { mode: ModeState | null }) {
  return (
    <AppFrame tab="Trade log" mode={mode}>
      <div className="page-head">
        <div>
          <h1>Trade log</h1>
          <div className="sub">Every attempt is recorded — successes, partials, failures and rejections alike.</div>
        </div>
        <div className="row">
          <Spot n={1}>
            <select defaultValue="last 7 days" tabIndex={-1} style={{ width: 140 }}>
              <option>last 7 days</option>
            </select>
          </Spot>
          <button type="button" tabIndex={-1} className="btn small">
            Refresh
          </button>
        </div>
      </div>

      <Spot n={2} block>
        <div className="grid cols-4">
          <div className="card stat">
            <div className="label">Realized P&amp;L</div>
            <div className="value green">
              0.00{' '}
              <span className="dim" style={{ fontSize: 13 }}>
                USDT
              </span>
            </div>
            <div className="hint">across 0 completed trades</div>
          </div>
          <div className="card stat">
            <div className="label">Unhedged partials</div>
            <div className="value">0</div>
            <div className="hint">no open exposure</div>
          </div>
          <div className="card stat">
            <div className="label">Opportunities detected</div>
            <div className="value">214</div>
            <div className="hint">1.3 per hour</div>
          </div>
          <div className="card stat">
            <div className="label">Best net seen</div>
            <div className="value green">2.41%</div>
            <div className="hint">avg 0.38%</div>
          </div>
        </div>
      </Spot>

      <div className="card">
        <div className="card-head">
          <Spot n={3} block>
            <div className="nav">
              <button type="button" tabIndex={-1}>
                Executed trades (0)
              </button>
              <button type="button" tabIndex={-1} className="active">
                Detected opportunities (214)
              </button>
            </div>
          </Spot>
        </div>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Detected</th>
                <th>Pair</th>
                <th>Route</th>
                <th className="num">Gross</th>
                <th className="num">Net</th>
                <th className="num">Peak</th>
                <th className="num">
                  <Spot n={4}>Lifetime</Spot>
                </th>
                <th>
                  <Spot n={5}>Alerted</Spot>
                </th>
              </tr>
            </thead>
            <tbody>
              {EPISODES.map((e) => (
                <tr key={e.at}>
                  <td className="faint">{e.at}</td>
                  <td className="sym">{e.sym}</td>
                  <td className="dim">{e.route}</td>
                  <td className="num dim">{e.gross}</td>
                  <td className="num">{e.net}</td>
                  <td className="num green">{e.peak}</td>
                  <td className={`num ${e.life < 1 ? 'amber' : ''}`}>{e.life.toFixed(1)}s</td>
                  <td className="faint">{e.alerted}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AppFrame>
  );
}

// ----------------------------------------------------------------- settings

const KEYS = [
  { label: 'Binance', masked: 'q7Hc••••••••Q9aP' },
  { label: 'OKX', masked: '3f0b••••••••e81c' },
  { label: 'KuCoin', masked: '65d2••••••••a4f0' },
  { label: 'MEXC', masked: null },
];

export function SettingsMock({ mode }: { mode: ModeState | null }) {
  return (
    <AppFrame tab="Settings" mode={mode}>
      <div className="page-head">
        <div>
          <h1>Settings</h1>
          <div className="sub">Stored in the database, not in .env — changes survive a redeploy.</div>
        </div>
      </div>

      <div className="grid cols-2">
        <div className="card">
          <div className="card-head">
            <h2>
              <Spot n={1}>Detection</Spot>
            </h2>
          </div>
          <div className="card-body">
            <Field label="Minimum net profit (%)" type="number" value={1.5} />
            <Field label="Slippage / safety buffer (%)" type="number" value={0.1} />
            <Field label="Staleness window (ms)" type="number" value={2000} />
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>
              <Spot n={2}>Trade sizing</Spot>
            </h2>
          </div>
          <div className="card-body">
            <Field label="Default trade size" type="number" value={500} />
            <Field label="Maximum trade size" type="number" value={1000} />
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>
              <Spot n={3}>Alerts</Spot>
            </h2>
          </div>
          <div className="card-body">
            <div className="field">
              <Tick label="Push alerts enabled" checked />
            </div>
            <div className="row">
              <div className="field" style={{ flex: 1 }}>
                <Tick label="Telegram" checked />
              </div>
              <div className="field" style={{ flex: 1 }}>
                <Tick label="Email" checked={false} />
              </div>
            </div>
            <Field label="Alert floor (%)" type="number" value={1.5} />
            <Field label="Cooldown per route (ms)" type="number" value={300000} />
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>
              <Spot n={4}>Exchange API keys</Spot>
            </h2>
          </div>
          <div className="card-body">
            <div className="notice warn">
              Create every key <strong>trade-enabled and withdrawal-disabled</strong>. Keys are encrypted at rest and
              are never shown again after saving.
            </div>
            {KEYS.map((k) => (
              <div key={k.label} style={{ marginBottom: 12 }}>
                <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <strong>{k.label}</strong>{' '}
                    {k.masked ? (
                      <span className="mono faint" style={{ fontSize: 12 }}>
                        {k.masked}
                      </span>
                    ) : (
                      <span className="pill">not configured</span>
                    )}
                  </div>
                  <button type="button" tabIndex={-1} className="btn small">
                    {k.masked ? 'Replace' : 'Add keys'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="row" style={{ marginBottom: 16 }}>
        <Spot n={5}>
          <button type="button" tabIndex={-1} className="btn primary">
            Save settings
          </button>
        </Spot>
        <button type="button" tabIndex={-1} className="btn">
          Discard changes
        </button>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>
            <Spot n={6}>Two-factor authentication</Spot>
          </h2>
          <span className="pill">disabled</span>
        </div>
        <div className="card-body">
          <p className="dim" style={{ marginTop: 0 }}>
            Optional, and worth it: this login is the only thing between the internet and an interface that places
            live orders.
          </p>
          <button type="button" tabIndex={-1} className="btn small">
            Set up
          </button>
        </div>
      </div>
    </AppFrame>
  );
}
